import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { ArrowLeft, ArrowRight, Leaf, Droplets, FlaskRound, Factory, Truck, Award, Info, X, Recycle, AlertTriangle, CheckCircle } from 'lucide-react';

const SustainabilityScreen = () => {
  const { currentProduct, setCurrentScreen } = useApp();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  if (!currentProduct) {
    return null;
  }

  const sustainability = currentProduct.sustainabilityDetails || {};
  const oldFormat = currentProduct.sustainabilityDetails?.fabricImpact ? false : true;

  // Handle both old and new format
  const sections = oldFormat ? [
    { key: 'fabricImpact', label: 'Fabric Impact', icon: Leaf, color: '#1B7340', data: currentProduct.sustainabilityDetails?.fabric_impact || {} },
    { key: 'waterFootprint', label: 'Water Footprint', icon: Droplets, color: '#3B7CB8', data: currentProduct.sustainabilityDetails?.water_footprint || {} },
    { key: 'chemicalsDyes', label: 'Chemicals & Dyes', icon: FlaskRound, color: '#7B5EA7', data: currentProduct.sustainabilityDetails?.chemicals_dyes || {} },
    { key: 'manufacturingEthics', label: 'Manufacturing Ethics', icon: Factory, color: '#996B3D', data: currentProduct.sustainabilityDetails?.manufacturing_ethics || {} },
    { key: 'transportFootprint', label: 'Transport Footprint', icon: Truck, color: '#B85C3B', data: currentProduct.sustainabilityDetails?.transport_footprint || {} },
    { key: 'brandCredibility', label: 'Brand Credibility', icon: Award, color: '#000000', data: currentProduct.sustainabilityDetails?.brand_credibility || {} }
  ] : [
    { key: 'fabricImpact', label: 'Fabric Impact', icon: Leaf, color: '#1B7340', data: sustainability.fabric_impact || {} },
    { key: 'waterFootprint', label: 'Water Footprint', icon: Droplets, color: '#3B7CB8', data: sustainability.water_footprint || {} },
    { key: 'chemicalsDyes', label: 'Chemicals & Dyes', icon: FlaskRound, color: '#7B5EA7', data: sustainability.chemicals_dyes || {} },
    { key: 'manufacturingEthics', label: 'Manufacturing Ethics', icon: Factory, color: '#996B3D', data: sustainability.manufacturing_ethics || {} },
    { key: 'transportFootprint', label: 'Transport Footprint', icon: Truck, color: '#B85C3B', data: sustainability.transport_footprint || {} },
    { key: 'brandCredibility', label: 'Brand Credibility', icon: Award, color: '#000000', data: sustainability.brand_credibility || {} }
  ];

  const overallScore = sustainability.overall_score || sustainability.overallIndex || 60;

  const getScoreColor = (score) => {
    if (score >= 70) return '#1B7340';
    if (score >= 40) return '#996B3D';
    return '#B85C3B';
  };

  return (
    <div className="min-h-screen bg-white pb-28">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-black/10 px-6 py-5 sticky top-0 z-10">
        <button
          onClick={() => setCurrentScreen('fabric-intelligence')}
          className="flex items-center gap-2 text-black/72 hover:text-black transition-colors mb-4"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          <span className="text-xs font-light tracking-wider">Back</span>
        </button>
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-1">Environmental Analysis</p>
        <h1 className="font-sans text-2xl font-light text-black">
          Sustainability
        </h1>
        <p className="text-black/55 text-xs font-light mt-1">
          {currentProduct.brand} · {currentProduct.name}
        </p>
      </div>

      {/* Overall Index */}
      <div className="px-6 py-6">
        <div className="premium-card rounded-3xl p-8 luxury-border text-center
          bg-gradient-to-br from-[#1B7340]/[0.02] to-transparent">
          <Leaf size={28} strokeWidth={1.5} className="mx-auto text-[#1B7340]/60 mb-3" />
          <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em] mb-3">Sustainability Index</p>
          <div className="flex items-baseline justify-center gap-1">
            <span 
              className="font-serif text-5xl font-light"
              style={{ color: getScoreColor(overallScore) }}
            >
              {Math.round(overallScore)}
            </span>
            <span className="text-gray-300 text-lg font-light">/100</span>
          </div>
          
          {/* Quick Stats */}
          <div className="flex justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
            {sustainability.biodegradable !== undefined && (
              <div className="text-center">
                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${sustainability.biodegradable ? 'bg-[#1B7340]/10' : 'bg-[#B85C3B]/10'}`}>
                  <Leaf size={14} className={sustainability.biodegradable ? 'text-[#1B7340]' : 'text-[#B85C3B]'} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Biodegradable</p>
                <p className="text-xs text-gray-600">{sustainability.biodegradable ? 'Yes' : 'No'}</p>
              </div>
            )}
            {sustainability.recyclable !== undefined && (
              <div className="text-center">
                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${sustainability.recyclable ? 'bg-[#1B7340]/10' : 'bg-[#B85C3B]/10'}`}>
                  <Recycle size={14} className={sustainability.recyclable ? 'text-[#1B7340]' : 'text-[#B85C3B]'} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Recyclable</p>
                <p className="text-xs text-gray-600">{sustainability.recyclable ? 'Yes' : 'Limited'}</p>
              </div>
            )}
            {sustainability.carbon_footprint_kg && (
              <div className="text-center">
                <div className="w-8 h-8 mx-auto rounded-full flex items-center justify-center bg-gray-50/5">
                  <span className="text-[10px] font-bold text-gray-500">CO₂</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Footprint</p>
                <p className="text-xs text-gray-600">{sustainability.carbon_footprint_kg}kg</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transparency Note */}
      {sustainability.transparency_note && (
        <div className="px-6 mb-4">
          <div className="bg-[#996B3D]/5 border border-[#996B3D]/10 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-[#996B3D] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-[#996B3D] mb-1">Data Transparency</p>
                <p className="text-xs text-gray-500 leading-relaxed">{sustainability.transparency_note}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certifications */}
      {sustainability.certifications?.length > 0 && (
        <div className="px-6 mb-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-2">Certifications to Look For</p>
          <div className="flex flex-wrap gap-2">
            {sustainability.certifications.map((cert, i) => (
              <span key={i} className="px-3 py-1.5 bg-[#1B7340]/5 text-[#1B7340] text-[10px] rounded-lg border border-[#1B7340]/10">
                {cert}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Sections */}
      <div className="px-6 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400">Detailed Breakdown</p>
        
        {sections.map((section, i) => {
          const Icon = section.icon;
          const score = section.data.score || 55;
          const percentage = Math.min(score, 100);
          const isExpanded = expandedSection === i;
          
          return (
            <div
              key={i}
              className="premium-card rounded-xl luxury-border overflow-hidden"
            >
              <div 
                onClick={() => setExpandedSection(isExpanded ? null : i)}
                className="p-4 cursor-pointer hover:bg-gray-50/[0.01] transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${section.color}08` }}
                  >
                    <Icon size={16} strokeWidth={1.5} style={{ color: section.color }} className="opacity-60" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-gray-900 text-sm font-medium tracking-wide">
                      {section.label}
                    </h3>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span 
                      className="font-serif text-lg font-light"
                      style={{ color: getScoreColor(score) }}
                    >
                      {Math.round(score)}
                    </span>
                    <span className="text-[10px] text-gray-300">▼</span>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="h-1.5 bg-gray-50/[0.03] rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ 
                      width: `${percentage}%`, 
                      backgroundColor: getScoreColor(score),
                      opacity: 0.6
                    }}
                  />
                </div>
                
                <p className="text-gray-400 text-xs font-light leading-relaxed">
                  {section.data.reason}
                </p>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/[0.01]">
                  <div className="space-y-3">
                    {section.data.land_use_impact && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Land Use Impact</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.land_use_impact}</p>
                      </div>
                    )}
                    {section.data.pesticide_use && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Pesticide Use</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.pesticide_use}</p>
                      </div>
                    )}
                    {section.data.liters_per_kg && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Water Usage</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.liters_per_kg}</p>
                      </div>
                    )}
                    {section.data.comparison && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Comparison</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.comparison}</p>
                      </div>
                    )}
                    {section.data.common_chemicals?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Common Chemicals</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {section.data.common_chemicals.map((chem, j) => (
                            <span key={j} className="px-2 py-0.5 bg-[#7B5EA7]/5 text-[#7B5EA7] text-[10px] rounded">
                              {chem}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {section.data.health_concerns && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Health Concerns</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.health_concerns}</p>
                      </div>
                    )}
                    {section.data.labor_standards && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Labor Standards</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.labor_standards}</p>
                      </div>
                    )}
                    {section.data.transparency_level && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Transparency Level</p>
                        <p className="text-xs text-gray-500 mt-1 capitalize">{section.data.transparency_level}</p>
                      </div>
                    )}
                    {section.data.estimated_km && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Transport Distance</p>
                        <p className="text-xs text-gray-500 mt-1">{section.data.estimated_km}</p>
                      </div>
                    )}
                    {section.data.known_initiatives?.length > 0 && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Known Initiatives</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {section.data.known_initiatives.map((init, j) => (
                            <span key={j} className="px-2 py-0.5 bg-[#1B7340]/5 text-[#1B7340] text-[10px] rounded">
                              {init}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {section.data.controversies && (
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Known Issues</p>
                        <p className="text-xs text-[#B85C3B]/70 mt-1">{section.data.controversies}</p>
                      </div>
                    )}
                    {section.data.data_source && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-[10px] text-gray-300">
                          <Info size={10} className="inline mr-1" />
                          Source: {section.data.data_source}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Button */}
      <div className="px-6 mt-6">
        <button
          onClick={() => setShowInfoModal(true)}
          className="w-full flex items-center justify-center gap-2 py-3 text-gray-500
            hover:text-gray-900 transition-colors duration-300"
        >
          <Info size={14} strokeWidth={1.5} />
          <span className="text-xs font-light tracking-wider">How we calculate sustainability scores</span>
        </button>
      </div>

      {/* Navigation */}
      <div className="px-6 mt-4">
        <button
          onClick={() => setCurrentScreen('alternatives-canva')}
          className="w-full bg-[#000000] hover:bg-[#000000] text-white py-4 px-6 rounded-2xl
            flex items-center justify-center gap-4 transition-all duration-300"
        >
          <span className="text-sm font-semibold tracking-[0.1em] uppercase">Next: Smart Alternatives</span>
          <ArrowRight size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div 
          className="fixed inset-0 bg-white/30 backdrop-blur-sm z-50 flex items-end justify-center"
          onClick={() => setShowInfoModal(false)}
        >
          <div 
            className="bg-gray-50 rounded-t-3xl w-full max-w-md p-7 animate-slide-up max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-xl font-light text-gray-900">
                Scoring Methodology
              </h3>
              <button
                onClick={() => setShowInfoModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50/[0.03]
                  text-gray-400 hover:bg-gray-50/[0.06] transition-all"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-4 text-xs text-gray-500 leading-relaxed">
              <p>
                Our sustainability scores are calculated using a combination of:
              </p>
              <ul className="space-y-2 pl-4">
                <li>• <strong>Fabric composition analysis</strong> - Environmental impact of raw materials</li>
                <li>• <strong>Manufacturing country standards</strong> - Labor and environmental regulations</li>
                <li>• <strong>Brand sustainability reports</strong> - Published commitments and certifications</li>
                <li>• <strong>Industry benchmarks</strong> - Comparison with category averages</li>
              </ul>
              <div className="p-3 bg-[#996B3D]/5 rounded-lg">
                <p className="text-[#996B3D]">
                  <AlertTriangle size={12} className="inline mr-1" />
                  <strong>Important:</strong> Some data is estimated when brand-specific information is not publicly available. We clearly indicate verified vs estimated data.
                </p>
              </div>
              <p>
                Scores range from 0 (poor) to 100 (excellent). A score of 55 represents industry average.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SustainabilityScreen;
